import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

export function useWindowSize(callback?: (res: { width: number; height: number }) => void) {
    const [size, setSize] = useState([window.innerWidth, window.innerHeight]);

    useEffect(() => {
        function updateSize() {
            const width = window.innerWidth;
            const height = window.innerHeight;

            setSize([width, height]);

            if (callback) {
                callback({ width, height });
            }
        }

        if ((window as any).TARGET === 'ipad' || (window as any).TARGET === 'android_tab') {
            const obs = new ResizeObserver(updateSize);
            const el = document.getElementById('resize-observable');

            if (el) {
                obs.observe(el);
            }

            return () => {
                obs.disconnect();
            };
        }

        window.addEventListener('resize', updateSize);

        return () => {
            window.removeEventListener('resize', updateSize);
        };
    }, []);

    return size;
}

export function useTimeout() {
    const timeout = useRef<any>();

    return (callback, ms, ...args) => {
        clearTimeout(timeout.current);
        timeout.current = setTimeout(callback, ms, ...args);
    };
}

export function useMobile(): boolean {
    const [width] = useWindowSize();

    return width <= 640;
}

export const popdownDirections = ['bottom', 'top', 'left', 'right', 'none'] as const;
export type PopdownDirections = (typeof popdownDirections)[number];

export interface PositionState {
    body: { left: number; top: number };
    arrow: { left: number; top: number };
    direction: PopdownDirections;
    room: number;
}

export interface PositioningConfig {
    directions?: PopdownDirections[];
    dropdownOffset?: number;
}

export function usePosition(
    targetRef: MutableRefObject<HTMLElement | null>,
    dropdownRef: MutableRefObject<HTMLElement | null>,
    config: PositioningConfig,
    show: boolean | number = true
) {
    const [state, setState] = useState<PositionState | null>(null);
    const [winWidth, winHeight] = useWindowSize();

    useLayoutEffect(() => {
        if (dropdownRef.current && targetRef.current && show) {
            if (config.directions?.includes('none')) {
                setState(null);

                return;
            }

            const targetRect = targetRef.current.getBoundingClientRect();
            const dropdownRect = dropdownRef.current.getBoundingClientRect();
            // [vertical, horizontal]
            const room = {
                top: [Math.min(targetRect.top, winHeight - targetRect.height), winWidth],
                left: [winHeight, targetRect.left],
                bottom: [winHeight - targetRect.bottom, winWidth],
                right: [winHeight, winWidth - targetRect.right]
            };
            const dropdownOffset = config.dropdownOffset ?? 0;
            const directions = config.directions ? config.directions : popdownDirections;

            const preferredRoom = directions.reduce((acc, key) => {
                if (key === 'none') {
                    return acc;
                }

                if (
                    room[key][1] >= dropdownRect.width + dropdownOffset &&
                    room[acc][0] < dropdownRect.height + dropdownOffset &&
                    room[key][0] > room[acc][0]
                ) {
                    return key;
                }

                return acc;
            }, directions[0]);

            if (
                Math.max(room.top[0], room.bottom[0]) < dropdownRect.height + dropdownOffset &&
                Math.max(room.left[1], room.right[1]) < dropdownRect.width + dropdownOffset
            ) {
                setState(null);
            } else {
                if (preferredRoom === 'bottom' || preferredRoom === 'top') {
                    let left = Math.max(targetRect.left + (targetRect.width - dropdownRect.width) / 2, dropdownOffset);

                    if (dropdownRect.width > winWidth) {
                        left = (winWidth - dropdownRect.width) / 2;
                    } else if (left + dropdownRect.width > winWidth) {
                        left = winWidth - dropdownRect.width;
                    }

                    const top =
                        preferredRoom === 'bottom'
                            ? targetRect.bottom + dropdownOffset
                            : targetRect.top - dropdownRect.height - dropdownOffset;
                    const maxHeight = room[preferredRoom][0] - dropdownOffset;

                    setState({
                        body: { left: left, top: top },
                        arrow: { left: targetRect.left + targetRect.width / 2, top: targetRect[preferredRoom] },
                        room: maxHeight,
                        direction: preferredRoom
                    });
                } else if (preferredRoom === 'right' || preferredRoom === 'left') {
                    let top = targetRect.top + (targetRect.height - dropdownRect.height) / 2;
                    const left =
                        preferredRoom === 'right'
                            ? targetRect.right + dropdownOffset
                            : targetRect.left - dropdownRect.width - dropdownOffset;

                    top = Math.min(top, winHeight - dropdownRect.height);
                    top = Math.max(top, 0);
                    const maxHeight = winHeight;

                    setState({
                        body: { left: left, top: top },
                        arrow: { left: targetRect[preferredRoom], top: targetRect.top + targetRect.height / 2 },
                        room: maxHeight,
                        direction: preferredRoom
                    });
                }
            }
        }
    }, [winWidth, winHeight, config.directions, config.dropdownOffset, show]);

    return state;
}
